import { Box, Typography } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BaseButton from "../../BaseButton";
import { formatDateOfBirth } from "../../../utils/format-date";

// Helper to format price strings with thousand separators (e.g., "$1234.00" -> "$1,234.00")
const formatPriceWithCommas = (priceStr) => {
  if (!priceStr) return priceStr;
  // Match the dollar sign, integer part, and decimal part
  const match = priceStr.match(/^(\$?)(\d+)(\.\d+)?$/);
  if (!match) return priceStr;
  const [, dollar, intPart, decimal] = match;
  const formattedInt = parseInt(intPart, 10).toLocaleString('en-US');
  return `${dollar}${formattedInt}${decimal || ''}`;
};

export default function ConfirmationStep({ data, onClose, kitPriceDollars, familyDiscountPriceDollars }) {
  // Determine the actual price based on whether family discount was applied
  const displayPrice = formatPriceWithCommas(
    data.applyFamilyDiscount ? familyDiscountPriceDollars : kitPriceDollars
  );
  return (
    <Box>
      {/* Success Icon */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 3
        }}
      >
        <CheckCircleOutlineIcon
          sx={{
            fontSize: '80px',
            color: '#39B195'
          }}
        />
      </Box>

      {/* Title */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            color: '#5A3BC7',
            fontSize: { xs: '24px', sm: '28px' },
            fontWeight: 600,
            mb: 1
          }}
        >
          Invitation Sent!
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#5B87EF',
            fontSize: '14px'
          }}
        >
          The patient will receive an email with instructions to complete their registration
        </Typography>
      </Box>

      {/* Invitation Details */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: '8px',
          border: '1px solid #39B195',
          background: '#F0FDF4'
        }}
      >
        {/* Email sent to */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EmailOutlinedIcon sx={{ color: '#39B195', mr: 2, fontSize: '24px' }} />
          <Box>
            <Typography variant="body2" sx={{ color: '#46545F', fontSize: '13px' }}>
              Invitation sent to:
            </Typography>
            <Typography variant="body1" sx={{ color: '#46545F', fontSize: '16px', fontWeight: 600 }}>
              {data.emailSentTo || data.email}
            </Typography>
          </Box>
        </Box>

      </Box>

      {/* Patient Summary */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: '8px',
          border: '1px solid #ECEFF1',
          background: '#F6F9FC'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#46545F',
            fontSize: '16px',
            fontWeight: 600,
            mb: 2
          }}
        >
          Patient Details
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
            Name:
          </Typography>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
            {data.patientFirstName} {data.patientLastName}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
            Date of Birth:
          </Typography>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
            {formatDateOfBirth(data.patientDateOfBirth)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
            Kit Code:
          </Typography>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600, fontFamily: 'monospace' }}>
            {data.kitCode}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
            Test Ordered:
          </Typography>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
            Whole Genome
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: '1px solid #ECEFF1',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
            Patient Price:
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" sx={{ color: '#5A3BC7', fontSize: '18px', fontWeight: 700 }}>
              {displayPrice}
            </Typography>
            {data.applyFamilyDiscount && (
              <Typography variant="caption" sx={{ color: '#4CAF50', fontSize: '12px' }}>
                Family Discount Applied
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* What Happens Next */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '8px',
          border: '1px solid #5B87EF',
          background: '#F0F4FF'
        }}
      >
        <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600, mb: 0.5 }}>
          What happens next:
        </Typography>
        <Typography variant="body2" sx={{ color: '#46545F', fontSize: '13px', lineHeight: 1.6 }}>
          The patient will complete payment via the email link. Once done, they'll appear in your patient list.
        </Typography>
      </Box>

      {/* Close Button */}
      <BaseButton
        onClick={onClose}
        buttonType="primary"
        sx={{
          width: '100%'
        }}
      >
        Done
      </BaseButton>
    </Box>
  );
}
